import React, {
  useState, useCallback, useEffect, useMemo, useRef, memo,
} from 'react'
import type { PostHogEventSignatures } from '../../../../PostHog/events/index'
import FollowTag from '../../../shared/FollowTag/index'
import PlaylistShareButton from '../../../shared/PlaylistShareButton/index'
import NotificationToast from '../../../../NotificationToast/index'
import {
  getToastExitClass,
} from '../../Library/PlaylistTabs/PlaylistTabContainer/toast-exit-utils'
import type {
  PlaylistSearchNode, EnrichedPlaylistItem,
} from '../../../../../services/community/types'
import type { PlaylistRowContextValue } from './PlaylistRowContext'
import PlaylistRow from './PlaylistRow'

/**
 * Variant: Discover tab row.
 *
 * Discover hydrates differently from custom / followed rows: the
 * upstream `useDiscoverTabState` transforms search results into
 * `EnrichedPlaylistItem[]` directly (`useRowHydration` is bypassed).
 * The variant carries:
 *   - the toast state machine (idle → fading → collapsing → dismissed)
 *   - the anchor-click capture handler for both the surface-specific
 *     `community_discover_video_clicked` and the generic
 *     `community_playlist_item_played` event
 *
 * PostHog tracking callbacks arrive as props from `ProfileDiscoverTab`
 * (`trackDiscoverPlaylistFollowed`, `trackDiscoverVideoClicked`,
 * `trackPlaylistItemPlayed`) — registered once at the tab parent so
 * per-row renders don't repeatedly call `usePostHogEvent`, matching the
 * `FollowedPlaylistRow` / `ProfileFollowingTab` pattern. The surface-
 * specific naming MUST stay `track*` per the project memory.
 *
 * Slot wiring preserves legacy `DiscoverPlaylistRow.tsx:240-283`
 * verbatim (FollowTag in `statusTag`, PlaylistShareButton in
 * `shareButton`). The plan's §9.6 column with no statusTag/shareButton
 * would have stripped the share affordance; not ratified — legacy
 * preserved.
 */

const DISCOVER_TOAST_FADE_MS = 300
const DISCOVER_TOAST_COLLAPSE_MS = 300

export interface DiscoverPlaylistRowProps {
  playlist: PlaylistSearchNode
  enrichedItems: EnrichedPlaylistItem[]
  totalDuration: string | null
  followedIds: Set<string>
  followerCounts: Record<string, number>
  unfollowToast: { playlistId: string; playlistName: string } | null
  authorDisplay: string
  onFollow: (id: string) => void
  onUnfollow: (id: string, name: string) => void
  onUndoUnfollow: (id: string) => void
  onDismissUnfollow: (id: string) => void
  seeMoreLabel: string
  showLessLabel?: string
  noLongerFollowingPrefix: string
  undoLabel: string
  /** Zero-based index of this row within the Discover results. */
  position: number
  trackDiscoverPlaylistFollowed: PostHogEventSignatures['community_discover_playlist_followed']
  trackDiscoverVideoClicked: PostHogEventSignatures['community_discover_video_clicked']
  trackPlaylistItemPlayed: PostHogEventSignatures['community_playlist_item_played']
}

function DiscoverPlaylistRowImpl ({
  playlist,
  enrichedItems,
  totalDuration,
  followedIds,
  followerCounts,
  unfollowToast,
  authorDisplay,
  onFollow,
  onUnfollow,
  onUndoUnfollow,
  onDismissUnfollow,
  seeMoreLabel,
  showLessLabel,
  noLongerFollowingPrefix,
  undoLabel,
  position,
  trackDiscoverPlaylistFollowed,
  trackDiscoverVideoClicked,
  trackPlaylistItemPlayed,
}: DiscoverPlaylistRowProps) {
  const isFollowed = followedIds.has(playlist.id)
  const followerCount = followerCounts[playlist.id] ?? playlist.followerCount ?? 0
  const isToastTarget = unfollowToast?.playlistId === playlist.id

  const rowRef = useRef<HTMLDivElement>(null)
  const lastHeightRef = useRef<number | null>(null)
  const [exitPhase, setExitPhase] = useState<'idle' | 'fading' | 'collapsing'>('idle')
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearAnimationTimers = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current)
      fadeTimerRef.current = null
    }
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current)
      collapseTimerRef.current = null
    }
  }, [])

  useEffect(() => () => { clearAnimationTimers() }, [clearAnimationTimers])

  useEffect(() => {
    if (!isToastTarget && rowRef.current) {
      lastHeightRef.current = rowRef.current.offsetHeight
    }
  }, [isToastTarget])

  useEffect(() => {
    if (!isToastTarget) setExitPhase('idle')
  }, [isToastTarget])

  const handleAnimatedDismiss = useCallback(() => {
    setExitPhase('fading')
    fadeTimerRef.current = setTimeout(() => {
      fadeTimerRef.current = null
      setExitPhase('collapsing')
      collapseTimerRef.current = setTimeout(() => {
        collapseTimerRef.current = null
        setExitPhase('idle')
        onDismissUnfollow(playlist.id)
      }, DISCOVER_TOAST_COLLAPSE_MS)
    }, DISCOVER_TOAST_FADE_MS)
  }, [onDismissUnfollow, playlist.id])

  const handleAnimatedUndo = useCallback(() => {
    clearAnimationTimers()
    setExitPhase('idle')
    onUndoUnfollow(playlist.id)
  }, [onUndoUnfollow, playlist.id, clearAnimationTimers])

  const handleRowClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null
    if (!target) return
    const anchor = target.closest('a[href]') as HTMLAnchorElement | null
    if (!anchor) return
    const href = anchor.getAttribute('href') || ''
    if (!href) return
    const tileIndex = enrichedItems.findIndex((item) => {
      const path = item.path || ''
      return !!path && (href === path || href.endsWith(path))
    })
    if (tileIndex === -1) return
    const clicked = enrichedItems[tileIndex]
    const contentId = clicked.contentId != null ? String(clicked.contentId) : ''
    if (!contentId) return
    const contentTitle = clicked.title || undefined
    const creatorDisplayName = playlist.userProfile?.displayName || undefined
    trackDiscoverVideoClicked(contentId, playlist.id, tileIndex, contentTitle)
    trackPlaylistItemPlayed(
      contentId,
      'discover',
      playlist.id,
      tileIndex,
      isFollowed ? 'followed' : 'other',
      undefined,
      contentTitle,
      creatorDisplayName,
    )
  }, [
    playlist.id,
    playlist.userProfile?.displayName,
    enrichedItems,
    isFollowed,
    trackDiscoverVideoClicked,
    trackPlaylistItemPlayed,
  ])

  const handleFollowFromDiscover = useCallback(() => {
    trackDiscoverPlaylistFollowed(playlist.id, position)
    onFollow(playlist.id)
  }, [trackDiscoverPlaylistFollowed, playlist.id, position, onFollow])

  const handleUnfollowClick = useCallback(
    () => onUnfollow(playlist.id, playlist.playlistName || ''),
    [onUnfollow, playlist.id, playlist.playlistName],
  )

  // Context value must be computed before the toast early-return so the
  // hook count stays constant across renders (rules-of-hooks). The
  // toast branch doesn't use ctx, but useMemo must still run.
  const ctx = useMemo<PlaylistRowContextValue>(() => ({
    playlistId: playlist.id,
    playlistName: playlist.playlistName || '',
    playlistType: playlist.playlistType || undefined,
    items: enrichedItems,
    expectedItemCount: playlist.itemCount ?? enrichedItems.length,
    totalDuration,
    isHydrating: false,
    isEditing: false,
  }), [
    playlist.id, playlist.playlistName, playlist.playlistType,
    playlist.itemCount, enrichedItems, totalDuration,
  ])

  // Memoise the header slots. Hook order must stay constant across the
  // toast-branch early return below, so these `useMemo`s sit before it —
  // the toast branch simply doesn't read the values. See `MyPlaylistRow.tsx`
  // for the canonical rationale behind memoising header slots.
  const headerStatusTag = useMemo(() => (
    <FollowTag
      isFollowing={isFollowed}
      followerCount={followerCount}
      onClick={isFollowed ? undefined : handleFollowFromDiscover}
      onUnfollow={isFollowed ? handleUnfollowClick : undefined}
    />
  ), [isFollowed, followerCount, handleFollowFromDiscover, handleUnfollowClick])

  const headerShareButton = useMemo(() => (
    <PlaylistShareButton
      playlistId={playlist.id}
      playlistName={playlist.playlistName || ''}
      description={playlist.description}
      items={enrichedItems}
    />
  ), [playlist.id, playlist.playlistName, playlist.description, enrichedItems])

  if (isToastTarget && unfollowToast?.playlistName) {
    const exitClass = getToastExitClass(exitPhase)
    return (
      <div
        ref={rowRef}
        className={`playlist-tab-container__playlist-row playlist-tab-container__playlist-row--toast${exitClass}`}
        style={lastHeightRef.current ? { minHeight: lastHeightRef.current } : undefined}
      >
        <NotificationToast
          duration={10000}
          message={(
            <span>
              {noLongerFollowingPrefix}
              <strong>{unfollowToast.playlistName}</strong>
            </span>
          )}
          variant="success"
          onUndo={handleAnimatedUndo}
          undoLabel={undoLabel}
          onDismiss={handleAnimatedDismiss}
        />
      </div>
    )
  }

  return (
    <PlaylistRow.Provider value={ctx}>
      <PlaylistRow.Frame
        className="playlist-tab-container__playlist-row"
        innerRef={rowRef}
        onClickCapture={handleRowClickCapture}
        role="presentation"
      >
        <PlaylistRow.Header
          authorDisplay={authorDisplay}
          authorUid={playlist.userProfile?.uuid || null}
          followerCount={followerCount}
          statusTag={headerStatusTag}
          shareButton={headerShareButton}
        />
        <PlaylistRow.Body
          seeMoreLabel={seeMoreLabel}
          showLessLabel={showLessLabel}
        />
      </PlaylistRow.Frame>
    </PlaylistRow.Provider>
  )
}

const DiscoverPlaylistRow = memo(DiscoverPlaylistRowImpl)
DiscoverPlaylistRow.displayName = 'DiscoverPlaylistRow'
export default DiscoverPlaylistRow
